<template>
  <BaseModal :modal-name="setNewPasswordModal" :title="_T('@Change Password')">
    <MyForm
      id="new-password"
      name="new-password"
      :schema="schema"
      :state="form"
      class="set-password"
      @submit="onSetPassword"
    >
      <MyInput
        v-model="form.password"
        name="password"
        caption="@New Password"
        autocomplete="new-password"
      />
      <MyInput
        v-model="form.passwordConfirmation"
        name="passwordConfirmation"
        caption="@Password confirmation"
        autocomplete="new-password"
      />
      <ButtonPrimary
        button-width="268px"
        type="submit"
        :aria-label="_T('@Will confirm')"
        :disabled="isHandled"
      >
        {{ _T("@Will confirm") }}
      </ButtonPrimary>
    </MyForm>
  </BaseModal>
</template>

<script setup>
import ButtonPrimary from "~/modules/shared/buttons/ButtonPrimary.vue";
import {
  successfulModal,
  unsuccessfulModal,
  useModalStore,
  setNewPasswordModal,
} from "~/modules/shared/modals/store/modalStore";
import { useUserStore } from "~/store/userStore";
import { useSingletonHandler } from "~/uses/useSingletonHandler";
import { useAPI } from "~/uses/useMyFetch";
import BaseModal from "~/modules/shared/modals/BaseModal.vue";
import { z } from "zod";
import { FORBIDDEN_ERROR } from "~/nuxt_modules/common/constants/index.js";
import MyForm from "~/nuxt_modules/common/ui/components/form/MyForm.vue";
import MyInput from "~/nuxt_modules/common/ui/components/form/MyInput.vue";

const route = useRoute();

const modalStore = useModalStore();
const userStore = useUserStore();

const successfulData = {
  title: _T("@Change Password"),
  description: _T("@Password changed successfully"),
  isWithTimeout: true,
};

const unsuccessfulData = {
  title: _T("@Change Password"),
  description: _T("@Password changed unsuccessful"),
  isWithTimeout: false,
};

const schema = z
  .object({
    password: z
      .string(_T("@Entry field required"))
      .min(6, `${_T("@Minimum password length")} 6`)
      .regex(/\d/, _T("@The password must contain at least one number")),
    passwordConfirmation: z.string(_T("@Entry field required")),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.passwordConfirmation) {
      ctx.addIssue({
        path: ["passwordConfirmation"],
        message: _T("@Password mismatch"),
      });
    }
  });

const form = reactive({
  password: undefined,
  passwordConfirmation: undefined,
});

const { isHandled, handle } = useSingletonHandler(async (data) => {
  await useAPI("/account/password/reset/complete", {
    method: "POST",
    body: {
      token: route.query.recovery,
      newPassword: data.password,
    },
  });
});

async function onSetPassword(data) {
  try {
    await handle(data);

    userStore.fetch();

    modalStore.toggleModal(setNewPasswordModal);
    modalStore.toggleModal(successfulModal, successfulData);
  } catch (error) {
    if (
      FORBIDDEN_ERROR.ACCOUNT_PASSWORD_RESET_INVALID_TOKEN === error.data.code
    ) {
      modalStore.toggleModal(setNewPasswordModal);
      modalStore.toggleModal(unsuccessfulModal, unsuccessfulData);
    }
  }
}
</script>

<style lang="scss" scoped>
.set-password {
  @include flex-container(column, center, center);
  gap: 16px;
}
</style>
